// Карточка обратной связи (CASE-48). Открывается поверх любого экрана:
// выбор «замечание / идея», текст, отправка. После успеха — подтверждение и
// возврат на тот же экран. Нет сети — текст сохраняется черновиком (localStorage),
// чтобы не набирать заново.

import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { T } from '../../tokens';
import { feedback, humanizeError } from '../../api';
import type { FeedbackKind } from '../../api/endpoints/feedback';

export interface FeedbackCardProps {
  /** Идентификатор экрана, с которого открыта карточка. */
  readonly screen: string;
  readonly onClose: () => void;
}

const DRAFT_KEY = 'aidepo_feedback_draft';

function readDraft(): string {
  try {
    return localStorage.getItem(DRAFT_KEY) ?? '';
  } catch {
    return '';
  }
}

function writeDraft(text: string): void {
  try {
    if (text.length > 0) localStorage.setItem(DRAFT_KEY, text);
    else localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* private mode — не критично */
  }
}

export function FeedbackCard({ screen, onClose }: FeedbackCardProps): JSX.Element {
  const [kind, setKind] = useState<FeedbackKind>('remark');
  const [text, setText] = useState<string>(() => readDraft());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  // Сохраняем черновик на каждое изменение — переживает закрытие карточки.
  useEffect(() => {
    if (!sent) writeDraft(text);
  }, [text, sent]);

  // После успешной отправки — короткое подтверждение, затем возврат на экран.
  useEffect(() => {
    if (!sent) return;
    const t = setTimeout(onClose, 1400);
    return () => clearTimeout(t);
  }, [sent, onClose]);

  const canSend = text.trim().length > 0 && !busy;

  const send = (): void => {
    if (!canSend) return;
    setBusy(true);
    setError(null);
    feedback
      .submit(screen, kind, text.trim())
      .then(() => {
        writeDraft('');
        setSent(true);
      })
      .catch((err: unknown) => {
        // Текст остаётся в поле и в черновике — набирать заново не нужно.
        setError(humanizeError(err, 'save'));
        setBusy(false);
      });
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(31,27,22,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        zIndex: 60,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 380,
          background: T.surface,
          borderRadius: 18,
          border: `1px solid ${T.border}`,
          boxShadow: T.shadow3,
          padding: 18,
          fontFamily: T.font,
        }}
      >
        {sent ? (
          <div style={{ textAlign: 'center', padding: '18px 8px' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.ink }}>Спасибо!</div>
            <div style={{ fontSize: 13, color: T.ink3, marginTop: 6 }}>
              Обращение отправлено
            </div>
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 14,
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 700, color: T.ink }}>Обратная связь</div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Закрыть"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 14,
                  border: 'none',
                  background: T.surfaceAlt,
                  color: T.ink2,
                  cursor: 'pointer',
                  fontSize: 16,
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <KindTab
                active={kind === 'remark'}
                label="Замечание"
                onClick={() => setKind('remark')}
              />
              <KindTab
                active={kind === 'idea'}
                label="Предложить функцию"
                onClick={() => setKind('idea')}
              />
            </div>

            <textarea
              value={text}
              rows={4}
              disabled={busy}
              placeholder={
                kind === 'remark'
                  ? 'Что показалось неудобным или сломанным?'
                  : 'Какой функции не хватает?'
              }
              onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
              style={{
                width: '100%',
                padding: '12px 14px',
                borderRadius: 12,
                border: `1px solid ${T.border}`,
                fontFamily: T.font,
                fontSize: 14.5,
                color: T.ink,
                background: busy ? T.surfaceAlt : T.surface,
                outline: 'none',
                boxSizing: 'border-box',
                resize: 'none',
              }}
            />

            {error !== null && (
              <div
                style={{
                  marginTop: 10,
                  padding: '10px 12px',
                  background: T.dangerSoft,
                  border: '1px solid #E9C7BF',
                  borderRadius: 12,
                  fontSize: 12.5,
                  color: T.danger,
                  fontWeight: 600,
                }}
              >
                {error} Текст сохранён — можно отправить позже.
              </div>
            )}

            <button
              type="button"
              onClick={send}
              disabled={!canSend}
              style={{
                marginTop: 14,
                width: '100%',
                padding: '12px 16px',
                borderRadius: 12,
                border: 'none',
                background: canSend ? T.brand : T.borderStrong,
                color: '#FFFFFF',
                fontFamily: T.font,
                fontSize: 15,
                fontWeight: 700,
                cursor: canSend ? 'pointer' : 'default',
              }}
            >
              {busy ? 'Отправляю…' : 'Отправить'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function KindTab({
  active,
  label,
  onClick,
}: {
  readonly active: boolean;
  readonly label: string;
  readonly onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        padding: '9px 8px',
        borderRadius: 10,
        border: `1px solid ${active ? T.brand : T.border}`,
        background: active ? T.brandSoft : T.surface,
        color: active ? T.brandDeep : T.ink2,
        fontFamily: T.font,
        fontSize: 12.5,
        fontWeight: 700,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}
